import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  ListSubheader,
  MenuItem,
  TextField,
  Theme,
  Typography,
  createStyles,
  makeStyles
} from '@material-ui/core';
import { groupBy } from 'lodash';
import { IGroupProduct, IProduct, ITaxRate } from 'surveyjs/common/types/products';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column'
    },
    title: {
      marginBottom: theme.spacing(3)
    },
    formFields: {
      display: 'flex',
      flexDirection: 'column',
      gap: '30px'
    },
    workersInput: {
      width: 315
    },
    payNow: {
      marginTop: 'auto'
    },
    price: {
      marginBottom: theme.spacing(3)
    },
    subheader: {
      lineHeight: '24px',
      paddingLeft: theme.spacing(1),
      position: 'relative'
    },
    menuItem: {
      padding: theme.spacing(0, 3)
    },
    vat: {
      fontSize: 13,
      fontWeight: theme.typography.fontWeightMedium,
      marginLeft: 5
    }
  })
);

type Props = {
  products: IProduct[];
  isFetching: boolean;
  handleSelectChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePayment: () => void;
  productSelect: string;
  totalPrice: number | string;
  taxRate: ITaxRate | null;
  isDisable: boolean;
  isTaxIncluded: boolean;
};

export const OnlineTrainingForm: React.FC<Props> = ({
  products,
  isFetching,
  productSelect,
  totalPrice,
  taxRate,
  isDisable,
  isTaxIncluded,
  handleSelectChange,
  handleInputChange,
  handlePayment
}) => {
  const { t } = useTranslation('subscriber');
  const classes = useStyles();

  const getSubheader = (item: IProduct): string => item.name?.split('-')[1] ?? '';

  const renderOptions = () => {
    const options = [];
    const groupProduct: IGroupProduct[] = products
      ?.filter(p => p.subscriptionTier === 'Human Focus')
      .sort((a, b) => getSubheader(a).localeCompare(getSubheader(b)))
      ?.map(p => ({
        ...p,
        groupBy: getSubheader(p),
        nameFormatted:
          p.name
            ?.split('-')
            .splice(2)
            .join('-') ?? ''
      }));

    for (const [product, option] of Object.entries(groupBy(groupProduct, 'groupBy'))) {
      options.push(
        <ListSubheader classes={{ root: classes.subheader }} key={product}>
          {product}
        </ListSubheader>
      );
      for (const item of option.sort((a, b) => a.nameFormatted.localeCompare(b.nameFormatted))) {
        options.push(
          <MenuItem classes={{ root: classes.menuItem }} key={item.id} value={item.id}>
            {item.nameFormatted}
          </MenuItem>
        );
      }
    }
    return options;
  };

  return (
    <Box className={classes.container}>
      <Typography variant="subtitle1" classes={{ root: classes.title }}>
        {t(
          'onlineTraining.form.title',
          'Simply enter the number of required trainees and choose the course or bundle you’d like to purchase.'
        )}
      </Typography>
      <form className={classes.formFields}>
        <TextField
          id="workers"
          label={t('onlineTraining.form.workersInputLabel', 'Number of workers to be trained')}
          variant="filled"
          className={classes.workersInput}
          type="number"
          InputProps={{
            inputProps: { min: 0 }
          }}
          onChange={handleInputChange}
        />
        <TextField
          id="filled-select"
          select
          label={t('onlineTraining.form.courseOrBundleInputLabel', 'Course or bundle')}
          value={productSelect}
          onChange={handleSelectChange}
          variant="filled"
        >
          {isFetching ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            renderOptions()
          )}
        </TextField>
      </form>

      <Box className={classes.payNow}>
        <Typography variant="h5" className={classes.price}>
          {t('onlineTraining.form.total', 'Total:')} {`${totalPrice}`}
          {taxRate && isTaxIncluded && totalPrice !== '£0.00' && (
            <span className={classes.vat}>(inc {taxRate.name})</span>
          )}
        </Typography>
        <Button color="primary" variant="contained" disabled={isDisable} onClick={handlePayment}>
          {t('onlineTraining.form.pay', 'Pay now')}
        </Button>
      </Box>
    </Box>
  );
};
